import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Shield, Cookie, BarChart, Mail, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";

const CONSENT_VERSION = "v1.0";

interface ConsentOnboardingProps {
  onComplete: () => void;
}

export const ConsentOnboarding = ({ onComplete }: ConsentOnboardingProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consents, setConsents] = useState({
    functional: true, // Always required
    analytics: false,
    marketing: false,
    chatbot: false,
  });

  const handleSubmit = async () => {
    if (!user) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from('user_consent').upsert({
        user_id: user.id,
        consent_version: CONSENT_VERSION,
        functional_consent: consents.functional,
        analytics_consent: consents.analytics,
        marketing_consent: consents.marketing,
        chatbot_consent: consents.chatbot,
        user_agent: navigator.userAgent,
      }, { onConflict: 'user_id,consent_version' });

      if (error) throw error;

      // Store in localStorage as backup
      localStorage.setItem('gdpr-consent', JSON.stringify({
        ...consents,
        timestamp: new Date().toISOString(),
        version: CONSENT_VERSION
      }));

      toast({
        title: "Preferences saved",
        description: "Your privacy preferences have been recorded.",
      });

      onComplete();
    } catch (error) {
      console.error('Error saving consent:', error);
      toast({
        title: "Error",
        description: "Failed to save preferences. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center py-8">
        <Card className="max-w-2xl w-full shadow-lg my-auto">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary" />
              Privacy & Data Protection
            </CardTitle>
            <CardDescription>
              We respect your privacy. Please review and accept our data processing terms.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
          <ScrollArea className="h-[300px] rounded-md border p-4">
            <div className="space-y-4 text-sm">
              <div>
                <h4 className="font-semibold mb-2">What data we collect:</h4>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Account information (name, email)</li>
                  <li>Energy consumption and usage patterns</li>
                  <li>Smart device data and control history</li>
                  <li>Billing and payment information</li>
                  <li>Login history and security logs</li>
                  <li>AI chatbot conversations (with your consent)</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">How we use your data:</h4>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Provide energy management services</li>
                  <li>Process payments and generate bills</li>
                  <li>Monitor and control your devices</li>
                  <li>Send important account notifications</li>
                  <li>Improve our services (with your consent)</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Your rights:</h4>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Access your data at any time</li>
                  <li>Export all your data</li>
                  <li>Delete your account and data</li>
                  <li>Change your consent preferences</li>
                </ul>
              </div>
            </div>
          </ScrollArea>

          <div className="space-y-4">
            <div className="flex items-start space-x-3 p-3 rounded-lg bg-muted/50">
              <Shield className="h-5 w-5 mt-0.5 text-primary" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <label className="font-medium text-sm">Essential Services (Required)</label>
                  <Checkbox checked disabled />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Necessary for authentication, security, and core app functionality
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-3 rounded-lg border">
              <BarChart className="h-5 w-5 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <label htmlFor="analytics" className="font-medium text-sm cursor-pointer">
                    Analytics & Performance
                  </label>
                  <Checkbox
                    id="analytics"
                    checked={consents.analytics}
                    onCheckedChange={(checked) => 
                      setConsents(prev => ({ ...prev, analytics: checked as boolean }))
                    }
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Help us improve the app by collecting anonymous usage data
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-3 rounded-lg border">
              <MessageSquare className="h-5 w-5 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <label htmlFor="chatbot" className="font-medium text-sm cursor-pointer">
                    AI Chatbot & Assistance
                  </label>
                  <Checkbox
                    id="chatbot"
                    checked={consents.chatbot}
                    onCheckedChange={(checked) => 
                      setConsents(prev => ({ ...prev, chatbot: checked as boolean }))
                    }
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Allow AI chatbot to process your messages through OpenAI/Google Gemini for energy assistance
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-3 rounded-lg border">
              <Mail className="h-5 w-5 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <label htmlFor="marketing" className="font-medium text-sm cursor-pointer">
                    Marketing Communications
                  </label>
                  <Checkbox
                    id="marketing"
                    checked={consents.marketing}
                    onCheckedChange={(checked) => 
                      setConsents(prev => ({ ...prev, marketing: checked as boolean }))
                    }
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Receive tips, updates, and promotional offers
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            By continuing, you agree to our{" "}
            <Link to="/privacy" className="underline hover:text-primary" target="_blank">
              Privacy Policy
            </Link>
            . You can change these preferences anytime in Settings → Privacy & Data.
          </p>
        </CardContent>

        <CardFooter className="flex gap-3">
          <Button
            variant="outline"
            onClick={async () => {
              const essentialOnly = { functional: true, analytics: false, marketing: false, chatbot: false };
              setConsents(essentialOnly);
              // Submit with essential only consents
              if (!user) return;
              setIsSubmitting(true);
              try {
                const { error } = await supabase.from('user_consent').upsert({
                  user_id: user.id,
                  consent_version: CONSENT_VERSION,
                  functional_consent: true,
                  analytics_consent: false,
                  marketing_consent: false,
                  chatbot_consent: false,
                  user_agent: navigator.userAgent,
                }, { onConflict: 'user_id,consent_version' });
                if (error) throw error;
                localStorage.setItem('gdpr-consent', JSON.stringify({
                  ...essentialOnly,
                  timestamp: new Date().toISOString(),
                  version: CONSENT_VERSION
                }));
                toast({
                  title: "Preferences saved",
                  description: "Essential cookies only. You can change this anytime.",
                });
                onComplete();
              } catch (error) {
                console.error('Error saving consent:', error);
                toast({
                  title: "Error",
                  description: "Failed to save preferences. Please try again.",
                  variant: "destructive",
                });
              } finally {
                setIsSubmitting(false);
              }
            }}
            disabled={isSubmitting}
            className="flex-1"
          >
            Essential Only
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex-1"
          >
            {isSubmitting ? "Saving..." : "Accept & Continue"}
          </Button>
        </CardFooter>
        </Card>
      </div>
    </div>
  );
};
